import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api, API } from '../App';
import QRCode from 'qrcode';
import { QrCode, Download, MapPin, ToggleLeft, ToggleRight, FileText } from 'lucide-react';

const getQrUrl = (uuid) => `${window.location.origin}/qr/${uuid}`;

async function generateQR(uuid, logoUrl, size = 300) {
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');

  await QRCode.toCanvas(canvas, getQrUrl(uuid), {
    width: size, margin: 2,
    color: { dark: '#0f172a', light: '#ffffff' }
  });

  if (logoUrl) {
    await new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const logoSize = size * 0.22;
        const x = (size - logoSize) / 2;
        const y = (size - logoSize) / 2;
        ctx.fillStyle = '#fff';
        ctx.roundRect(x - 6, y - 6, logoSize + 12, logoSize + 12, 8);
        ctx.fill();
        ctx.drawImage(img, x, y, logoSize, logoSize);
        resolve();
      };
      img.onerror = resolve;
      img.src = logoUrl;
    });
  }

  return canvas.toDataURL('image/png');
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// Uzun metni otomatik küçülten yardımcı
function autoFitText(ctx, text, maxWidth, maxSize, minSize = 22) {
  let size = maxSize;
  ctx.font = `bold ${size}px Arial, sans-serif`;
  while (ctx.measureText(text).width > maxWidth && size > minSize) {
    size -= 2;
    ctx.font = `bold ${size}px Arial, sans-serif`;
  }
  return size;
}

/**
 * Profesyonel A4 PDF çıktısı:
 *  • Üst: sol + sağ logo  |  kurum adı  |  temiz-iz.com alt etiketi
 *  • Orta: büyük QR kodu (yuvarlak köşeli çerçeve)
 *  • Alt: lokasyon adı, bina adı
 *  • Mesaj bandı
 *  • Footer: Copyright © temiz-iz.com
 */
async function downloadQRasPDF(qr, kurumLogoUrl) {
  // A4 @ 96dpi × 2 = 1587 × 2245  →  1190 × 1684 daha dengeli
  const W = 1190, H = 1684;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // ── Beyaz arka plan ───────────────────────────────────────
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  // ── Logo (kurum logosu varsa o, yoksa uygulama logosu) ────
  const appLogoUrl = `${window.location.origin}/logo.png`;
  const logoSrc = kurumLogoUrl || appLogoUrl;
  const LOGO_W = 130, LOGO_H = 130;
  const LOGO_Y = 56;

  const logoImg = await loadImage(logoSrc);
  if (logoImg) {
    // Sol logo
    ctx.drawImage(logoImg, 72, LOGO_Y, LOGO_W, LOGO_H);
    // Sağ logo
    ctx.drawImage(logoImg, W - 72 - LOGO_W, LOGO_Y, LOGO_W, LOGO_H);
  }

  // ── Kurum Adı ─────────────────────────────────────────────
  const TEXT_CENTER = W / 2;
  const kurumAdi = qr.kurum_adi || '';
  const maxTextW = W - 2 * (72 + LOGO_W + 24); // Logolar arasındaki alan

  ctx.textAlign = 'center';
  ctx.fillStyle = '#0f172a';
  const nameFontSize = autoFitText(ctx, kurumAdi, maxTextW, 50, 22);
  ctx.font = `bold ${nameFontSize}px Arial, sans-serif`;
  // Kurum adını logo alanının dikey ortasına hizala
  const nameY = LOGO_Y + LOGO_H / 2 + nameFontSize / 3;
  ctx.fillText(kurumAdi, TEXT_CENTER, nameY);

  // ── Ayraç çizgisi ─────────────────────────────────────────
  const SEP1_Y = LOGO_Y + LOGO_H + 36;
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(72, SEP1_Y);
  ctx.lineTo(W - 72, SEP1_Y);
  ctx.stroke();

  // ── QR Kodu ───────────────────────────────────────────────
  const QR_SIZE = 640;
  const QR_X = (W - QR_SIZE) / 2;
  const QR_Y = SEP1_Y + 40;

  // Kart arka planı (hafif gri, yuvarlatılmış köşe)
  ctx.fillStyle = '#f8fafc';
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(QR_X - 28, QR_Y - 28, QR_SIZE + 56, QR_SIZE + 56, 22);
  ctx.fill();
  ctx.stroke();

  // QR kodu (logo olmadan — daha temiz tarama)
  const qrDataUrl = await generateQR(qr.uuid, null, 640);
  const qrImg = await loadImage(qrDataUrl);
  if (qrImg) ctx.drawImage(qrImg, QR_X, QR_Y, QR_SIZE, QR_SIZE);

  // ── Lokasyon bilgileri ─────────────────────────────────────
  let infoY = QR_Y + QR_SIZE + 64;

  ctx.textAlign = 'center';
  ctx.fillStyle = '#0f172a';
  ctx.font = 'bold 52px Arial, sans-serif';
  ctx.fillText(qr.lokasyon_adi || '', TEXT_CENTER, infoY);
  infoY += 58;

  if (qr.bina_adi) {
    ctx.font = '30px Arial, sans-serif';
    ctx.fillStyle = '#475569';
    ctx.fillText(qr.bina_adi, TEXT_CENTER, infoY);
    infoY += 42;
  }

  // ── Mesaj bandı ───────────────────────────────────────────
  const MSG_TOP = infoY + 32;
  ctx.fillStyle = '#1e3a8a';
  ctx.beginPath();
  ctx.roundRect(72, MSG_TOP, W - 144, 64, 12);
  ctx.fill();

  ctx.font = 'bold 26px Arial, sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.fillText('QR KODU OKUTARAK BİZE BİLDİRİM GÖNDEREBİLİRSİNİZ', TEXT_CENTER, MSG_TOP + 40);

  // ── Footer ────────────────────────────────────────────────
  ctx.font = '20px Arial, sans-serif';
  ctx.fillStyle = '#94a3b8';
  ctx.textAlign = 'center';
  ctx.fillText('Copyright © temiz-iz.com', TEXT_CENTER, H - 44);

  // ── Canvas → JPEG → jsPDF ─────────────────────────────────
  const imgData = canvas.toDataURL('image/jpeg', 0.95);
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);
  pdf.save(`qr-${(qr.lokasyon_adi || 'kod').replace(/\s+/g, '-')}.pdf`);
}

export default function QRYonetim() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedBina, setSelectedBina] = useState('');
  const [qrDataUrls, setQrDataUrls] = useState({});
  const [selectedQr, setSelectedQr] = useState(null);
  const [generating, setGenerating] = useState({});
  const [pdfLoading, setPdfLoading] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/qr');
      setList(data);
    } catch { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const getLogoUrl = (qr) => {
    if (!qr.logo_ekle || !qr.kurum_logo) return null;
    return `${API.replace('/api', '')}/uploads/${qr.kurum_logo}`;
  };

  const getKurumLogoUrl = (qr) => {
    if (!qr.kurum_logo) return null;
    return `${API.replace('/api', '')}/uploads/${qr.kurum_logo}`;
  };

  const generateAndShow = async (qr) => {
    setGenerating(g => ({ ...g, [qr.uuid]: true }));
    try {
      const url = await generateQR(qr.uuid, getLogoUrl(qr));
      setQrDataUrls(prev => ({ ...prev, [qr.uuid]: url }));
      setSelectedQr({ ...qr, dataUrl: url });
    } catch { toast.error('QR oluşturulamadı'); }
    finally { setGenerating(g => ({ ...g, [qr.uuid]: false })); }
  };

  const downloadQR = async (qr) => {
    const url = qrDataUrls[qr.uuid] || await generateQR(qr.uuid, getLogoUrl(qr));
    const a = document.createElement('a');
    a.href = url;
    a.download = `qr-${qr.lokasyon_adi?.replace(/\s+/g, '-')}.png`;
    a.click();
    toast.success('QR PNG indirildi');
  };

  const downloadPDF = async (qr) => {
    setPdfLoading(p => ({ ...p, [qr.uuid]: true }));
    try {
      await downloadQRasPDF(qr, getKurumLogoUrl(qr));
      toast.success('PDF indirildi');
    } catch (e) {
      console.error(e);
      toast.error('PDF oluşturulamadı');
    } finally {
      setPdfLoading(p => ({ ...p, [qr.uuid]: false }));
    }
  };

  const toggleLogo = async (qr) => {
    try {
      await api.put(`/qr/${qr.uuid}/logo`, { logo_ekle: !qr.logo_ekle });
      toast.success('Güncellendi');
      load();
      setQrDataUrls(prev => { const n = { ...prev }; delete n[qr.uuid]; return n; });
    } catch { toast.error('Hata'); }
  };

  const binalar = [...new Set(list.filter(q => q.bina_adi).map(q => q.bina_adi))];
  const filtered = list.filter(q => {
    const binaMatch = !selectedBina || q.bina_adi === selectedBina;
    const searchMatch = !search || (`${q.lokasyon_adi} ${q.bina_adi || ''}`).toLowerCase().includes(search.toLowerCase());
    return binaMatch && searchMatch;
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">QR Yönetim</h1>
          <p className="page-subtitle">{list.length} lokasyon için kalıcı QR kodları</p>
        </div>
      </div>

      {/* Filtreler */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="search-box" style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
          <span className="search-icon">
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          </span>
          <input className="input" placeholder="Lokasyon ara..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input" style={{ maxWidth: 200 }} value={selectedBina} onChange={e => setSelectedBina(e.target.value)}>
          <option value="">Tüm Binalar</option>
          {binalar.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>

      <div className="qr-layout" style={{ display: 'grid', gridTemplateColumns: selectedQr ? '1fr 320px' : '1fr', gap: 20, alignItems: 'start' }}>
        {/* Liste */}
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Lokasyon</th>
                  <th>Bina</th>
                  <th>Kurum</th>
                  <th>Logo QR</th>
                  <th>UUID</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(q => (
                  <tr key={q.uuid} style={{ background: selectedQr?.uuid === q.uuid ? '#eff6ff' : '' }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MapPin size={14} color="var(--blue)" />
                        <span style={{ fontWeight: 600 }}>{q.lokasyon_adi}</span>
                      </div>
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--gray-600)' }}>{q.bina_adi || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--gray-600)' }}>{q.kurum_adi || '—'}</td>
                    <td>
                      <button
                        onClick={() => toggleLogo(q)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                        title={q.logo_ekle ? 'Logo var, kaldır' : 'Logo ekle'}
                      >
                        {q.logo_ekle
                          ? <ToggleRight size={22} color="var(--blue)" />
                          : <ToggleLeft size={22} color="var(--gray-400)" />
                        }
                        <span style={{ fontSize: 12, color: q.logo_ekle ? 'var(--blue)' : 'var(--gray-400)' }}>
                          {q.logo_ekle ? 'Açık' : 'Kapalı'}
                        </span>
                      </button>
                    </td>
                    <td>
                      <code style={{ fontSize: 11, color: 'var(--gray-400)', background: 'var(--gray-50)', padding: '2px 6px', borderRadius: 4 }}>
                        {q.uuid.slice(0, 8)}...
                      </code>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => generateAndShow(q)}
                          disabled={generating[q.uuid]}
                        >
                          {generating[q.uuid] ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <QrCode size={13} />}
                          Görüntüle
                        </button>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={() => downloadQR(q)} title="PNG İndir">
                          <Download size={14} />
                        </button>
                        <button
                          className="btn btn-secondary btn-sm btn-icon"
                          onClick={() => downloadPDF(q)}
                          disabled={pdfLoading[q.uuid]}
                          title="PDF İndir (A4 Baskı)"
                          style={{ color: 'var(--blue)' }}
                        >
                          {pdfLoading[q.uuid]
                            ? <div className="spinner" style={{ width: 14, height: 14 }} />
                            : <FileText size={14} />
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
                    {loading ? 'Yükleniyor...' : 'QR bulunamadı'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* QR Preview */}
        {selectedQr && (
          <div className="card" style={{ padding: 24, position: 'sticky', top: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>QR Önizleme</h3>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setSelectedQr(null)}>✕</button>
            </div>
            <div className="qr-wrapper">
              <img src={selectedQr.dataUrl} alt="QR" style={{ width: 240, height: 240, imageRendering: 'pixelated' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{selectedQr.lokasyon_adi}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>{selectedQr.bina_adi}</div>
              </div>
            </div>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => downloadQR(selectedQr)}>
                <Download size={15} /> PNG İndir
              </button>
              <button
                className="btn btn-secondary"
                style={{ width: '100%' }}
                onClick={() => downloadPDF(selectedQr)}
                disabled={pdfLoading[selectedQr.uuid]}
              >
                {pdfLoading[selectedQr.uuid]
                  ? <div className="spinner" style={{ width: 14, height: 14 }} />
                  : <FileText size={15} />
                }
                PDF İndir (A4 Baskı)
              </button>
              <div style={{ fontSize: 11, color: 'var(--gray-400)', textAlign: 'center', wordBreak: 'break-all' }}>
                {getQrUrl(selectedQr.uuid)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
